import XUIEditableTable, {
    XUIEditableTableBody,
    XUIEditableTableCellAutocompleterSecondarySearch,
    XUIEditableTableCellReadOnly,
    XUIEditableTableHead,
    XUIEditableTableHeadingCell,
    XUIEditableTableRow,
} from '@xero/xui/react/editabletable';
import XUIPicklist, { XUIPickitem } from '@xero/xui/react/picklist';
import { Dispatch, FC, ReactNode, SetStateAction, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { formatAccountButtonContent, formatCoAListItem } from '../../helpers/accountDropdown';
import {
    formatTaxRateButtonContent,
    formatTaxRateListItem,
    validateTaxRateAndAccount,
} from '../../helpers/taxRateDropdown';
import { IAccount, IAccountAndTaxMapping, IShopifySettingsResponse, IShopifyTaxRate, IXeroTaxRate } from '../../models';
import { installationSelectors } from '../../redux/slices/InstallationSlice';
import { settingsSelectors } from '../../redux/slices/SettingsSlice';
import { regionPriority } from '../../static/constants/regionPriority';
import { transactionPriority } from '../../static/constants/transactionPriority';
import lang from '../../static/lang/en-NZ';
import { InfoPopover } from '../InfoPopover';
import './index.css';

export interface ITaxMappingTableProps {
    hideRegionColumn: boolean;
    accountAndTaxMappings: IAccountAndTaxMapping[];
    allChartOfAccounts: IAccount[];
    allTaxRates: IXeroTaxRate[];
    onSetAccountAndTaxMapping: (taxMappings: IAccountAndTaxMapping[]) => void;
    setMappingValid: (isValid: boolean) => void;
    setCheckBoxChecked: (isValid: boolean) => void;
}

export const TaxMappingTable: FC<ITaxMappingTableProps> = ({
    hideRegionColumn,
    accountAndTaxMappings,
    allChartOfAccounts,
    allTaxRates,
    onSetAccountAndTaxMapping,
    setMappingValid,
    setCheckBoxChecked,
}) => {
    const installation = useSelector(installationSelectors.installation);
    const settings = useSelector(settingsSelectors.settings) as IShopifySettingsResponse;
    const integrationLang: any = lang[installation.integrationType];
    const [tableRows, setTableRows] = useState(accountAndTaxMappings);
    const [chartOfAccountSearch, setChartOfAccountSearch] = useState('');
    const [taxRateSearch, setTaxRateSearch] = useState('');
    const clearInputSearch = (setState: Dispatch<SetStateAction<string>>) => setState('');
    const defaultGuid = '00000000-0000-0000-0000-000000000000';

    useEffect(() => {
        if (!tableRows) {
            return;
        }

        const sortedTableRows = [...tableRows].sort((a, b) => {
            if (regionPriority[a.region] > regionPriority[b.region]) {
                return 1;
            } else if (regionPriority[a.region] < regionPriority[b.region]) {
                return -1;
            }

            return transactionPriority[a.transactionType] - transactionPriority[b.transactionType];
        });

        setTableRows([...sortedTableRows]);

        const allXeroAccountIds = [defaultGuid, ...allChartOfAccounts.map((x) => x.accountId)];
        const allXeroTaxTypes = allTaxRates.map((x) => x.taxType);

        const unselectedCells = tableRows.find((x) => !x.xeroAccountId || !x.xeroTaxType);
        const allTableAccountIds = tableRows.map((x) => x.xeroAccountId);
        const allTableTaxTypes = tableRows.map((x) => x.xeroTaxType);
        const deletedAccountRows = allTableAccountIds.find((x) => allXeroAccountIds.indexOf(x!) < 0);
        const deletedTaxRateRows = allTableTaxTypes.find((x) => allXeroTaxTypes.indexOf(x!) < 0);
        if (unselectedCells || deletedAccountRows || deletedTaxRateRows) {
            setMappingValid(false);
            return;
        }
        setMappingValid(true);
        // All dependencies used in a hook should be injected here.
        // This is to ensure that the application reacts to all state and state does not get out of sync.
        // When working in this hook please take the time to refactor, remove this comment and resolve the dependencies
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [settings.accountAndTaxMappings]);

    const handleChartOfAccountSelected = (selectedAccount: IAccount, row: IAccountAndTaxMapping) => () => {
        clearInputSearch(setChartOfAccountSearch);
        const index = findTaxMappingsIndex(row);
        const updatedTableRows = tableRows.map((currentRow, currentIndex) => {
            const tableRow = { ...currentRow };
            if (currentIndex === index) {
                tableRow.xeroAccountId = selectedAccount.accountId;
                tableRow.useRecommendedAccount = tableRow.recommendedAccount
                    ? tableRow.xeroAccountId === tableRow.recommendedAccount.accountId
                    : false;
            }
            return tableRow;
        });
        setTableRows([...updatedTableRows]);
        onSetAccountAndTaxMapping(updatedTableRows);
        setCheckBoxChecked(false);
    };

    const handleTaxRateSelected = (selectedTaxRate: IXeroTaxRate, row: IAccountAndTaxMapping) => () => {
        clearInputSearch(setChartOfAccountSearch);
        const index = findTaxMappingsIndex(row);
        const updatedTableRows = tableRows.map((currentRow, currentIndex) => {
            const tableRow: IAccountAndTaxMapping = { ...currentRow };
            if (currentIndex === index) {
                tableRow.xeroTaxType = selectedTaxRate.taxType;
            }
            return tableRow;
        });
        setTableRows([...updatedTableRows]);
        onSetAccountAndTaxMapping(updatedTableRows);
        setCheckBoxChecked(false);
    };

    const findTaxMappingsIndex = (row: IAccountAndTaxMapping) => {
        return tableRows.findIndex(
            (item: { transactionType: string; region: string; shopifyTaxRate?: IShopifyTaxRate }) =>
                item.shopifyTaxRate != null
                    ? item.shopifyTaxRate!.ratePercentage === row.shopifyTaxRate?.ratePercentage &&
                      item.shopifyTaxRate!.title === row.shopifyTaxRate.title &&
                      item.transactionType === row.transactionType &&
                      item.region === row.region
                    : item.transactionType === row.transactionType && item.region === row.region
        );
    };

    const handleChartOfAccountOnBlur = () => {
        clearInputSearch(setChartOfAccountSearch);
    };

    const handleTaxRateOnBlur = () => {
        clearInputSearch(setTaxRateSearch);
    };

    const taxRateMatchesAccountType = (row: IAccountAndTaxMapping): boolean => {
        const rowAccounts = row.recommendedAccount
            ? [row.recommendedAccount, ...allChartOfAccounts]
            : allChartOfAccounts;
        const rowAccount = rowAccounts.find((x) => x.accountId === row.xeroAccountId);
        const rowTaxRate = allTaxRates.find((x) => x.taxType === row.xeroTaxType);

        let valid: boolean = true;

        if (rowTaxRate && rowAccount) {
            valid = validateTaxRateAndAccount(rowAccount, rowTaxRate);
        }

        if (!valid) {
            setMappingValid(false);
            return false;
        }

        return valid;
    };

    const createRow = (row: IAccountAndTaxMapping, index: number): ReactNode => {
        // add the recommended account to the accounts drop down if it hasn't been created yet.
        const rowAccounts =
            row.recommendedAccount && row.recommendedAccount.accountId === defaultGuid
                ? [row.recommendedAccount!, ...allChartOfAccounts]
                : allChartOfAccounts;

        return (
            <XUIEditableTableRow key={index} className='tax-mapping-row'>
                {hideRegionColumn ? null : (
                    <XUIEditableTableCellReadOnly>
                        <div className='readonly-row'>{row.region}</div>
                    </XUIEditableTableCellReadOnly>
                )}
                <XUIEditableTableCellReadOnly>
                    <div className='readonly-row'>
                        {row.shopifyTaxRate
                            ? row.transactionType + ' ' + row.shopifyTaxRate.ratePercentage + '%'
                            : row.transactionType}
                    </div>
                </XUIEditableTableCellReadOnly>
                <XUIEditableTableCellAutocompleterSecondarySearch
                    buttonContent={formatAccountButtonContent(
                        rowAccounts,
                        row.xeroAccountId,
                        integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.ACCOUNT_CELL_PLACEHOLDER
                    )}
                    onSearch={setChartOfAccountSearch}
                    onClose={handleChartOfAccountOnBlur}
                    matchTriggerWidth={true}
                    validationMessage={integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.ACCOUNT_CELL_VALIDATION_MESSAGE}
                    isInvalid={!rowAccounts.find((x) => x.accountId === row.xeroAccountId)}
                    placeholder='Search'
                    inputLabel={'[Placeholder] Search'}
                >
                    <XUIPicklist>
                        {rowAccounts
                            ?.filter(
                                (f) =>
                                    chartOfAccountSearch === '' ||
                                    `${f.code}-${f.name}`.toLowerCase().includes(chartOfAccountSearch.toLowerCase())
                            )
                            .map((account, i) => (
                                <XUIPickitem
                                    key={i + 1}
                                    id={String(i + 1)}
                                    onClick={handleChartOfAccountSelected(account, row)}
                                    onSelect={handleChartOfAccountSelected(account, row)}
                                    isSelected={row.xeroAccountId === account.accountId}
                                >
                                    {formatCoAListItem(account)}
                                </XUIPickitem>
                            ))}
                    </XUIPicklist>
                </XUIEditableTableCellAutocompleterSecondarySearch>
                <XUIEditableTableCellAutocompleterSecondarySearch
                    buttonContent={formatTaxRateButtonContent(
                        allTaxRates,
                        row.xeroTaxType,
                        integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.TAX_RATE_CELL_PLACEHOLDER
                    )}
                    onSearch={setTaxRateSearch}
                    onClose={handleTaxRateOnBlur}
                    matchTriggerWidth={true}
                    placeholder='Search'
                    validationMessage={
                        !allTaxRates.find((x) => x.taxType === row.xeroTaxType)
                            ? integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.TAX_RATE_CELL_VALIDATION_MESSAGE
                            : integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE
                                  .TAX_RATE_ACCOUNT_TYPE_MISMATCH_VALIDATION_MESSAGE
                    }
                    isInvalid={
                        !allTaxRates.find((x) => x.taxType === row.xeroTaxType) || !taxRateMatchesAccountType(row)
                    }
                    inputLabel={'[Placeholder] Search'}
                >
                    <XUIPicklist>
                        {allTaxRates
                            .filter(
                                (f) =>
                                    taxRateSearch === '' || f.name.toLowerCase().includes(taxRateSearch.toLowerCase())
                            )
                            .map((taxRate, i) => (
                                <XUIPickitem
                                    key={i + 1}
                                    id={String(i + 1)}
                                    onClick={handleTaxRateSelected(taxRate, row)}
                                    onSelect={handleTaxRateSelected(taxRate, row)}
                                    isSelected={row.xeroTaxType === taxRate.taxType}
                                >
                                    {formatTaxRateListItem(taxRate)}
                                </XUIPickitem>
                            ))}
                    </XUIPicklist>
                </XUIEditableTableCellAutocompleterSecondarySearch>
            </XUIEditableTableRow>
        );
    };

    return (
        <div className='table-content'>
            <XUIEditableTable ariaLabel={'Select tax rates'}>
                <XUIEditableTableHead>
                    <XUIEditableTableRow>
                        {hideRegionColumn ? null : (
                            <XUIEditableTableHeadingCell>
                                {integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.REGION_HEADING}
                                <InfoPopover>
                                    {integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.REGION_POPOVER_CONTENT}
                                </InfoPopover>
                            </XUIEditableTableHeadingCell>
                        )}
                        <XUIEditableTableHeadingCell>
                            {integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.TRANSACTION_TYPE_HEADING}
                            <InfoPopover>
                                {integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.TRANSACTION_TYPE_POPOVER_CONTENT}
                            </InfoPopover>
                        </XUIEditableTableHeadingCell>
                        <XUIEditableTableHeadingCell>
                            {integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.ACCOUNT_HEADING}
                            <InfoPopover>
                                {integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.ACCOUNT_POPOVER_CONTENT}
                            </InfoPopover>
                        </XUIEditableTableHeadingCell>
                        <XUIEditableTableHeadingCell>
                            {integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.TAX_RATE_HEADING}
                            <InfoPopover>
                                {integrationLang.SETUP_PAGE.TAX_MAPPING_TABLE.TAX_RATE_POPOVER_CONTENT}
                            </InfoPopover>
                        </XUIEditableTableHeadingCell>
                    </XUIEditableTableRow>
                </XUIEditableTableHead>
                <XUIEditableTableBody>{tableRows ? tableRows.map(createRow) : []}</XUIEditableTableBody>
            </XUIEditableTable>
        </div>
    );
};
